import { useState, useEffect } from 'react'
import styles from './EditGroupModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'
import { formatDisplayDate } from '../../utils/dateUtils'

export default function EditGroupModal({ 
    isOpen, 
    onClose, 
    onSave,
    group,
    apiToken = null
}) {
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    })
    
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !loading)

    // Initialize form data with group values when modal opens
    useEffect(() => {
        if (isOpen && group) {
            setFormData({
                name: group.name || '',
                description: group.description || ''
            })
            setErrors({})
        }
    }, [isOpen, group])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}
        
        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = 'Group name is required'
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Group name must be at least 2 characters long'
        } else if (formData.name.trim().length > 100) {
            newErrors.name = 'Group name must be less than 100 characters'
        }
        
        // Description validation (optional but with limits)
        if (formData.description && formData.description.trim().length > 500) {
            newErrors.description = 'Description must be less than 500 characters'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }

        if (!group || !group.id) {
            setErrors({ general: 'Group information is missing' })
            return
        }

        setLoading(true)

        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }

            apiClient.setAuthToken(apiToken)
            
            const groupData = {
                name: formData.name.trim(),
                description: formData.description.trim() || null
            }

            const response = await apiClient.updateGroup(group.id, groupData)
            
            // Notify parent component of successful update
            if (onSave) {
                onSave(response.group || { ...group, ...groupData })
            }
            
            onClose()
        } catch (err) {
            console.error('Failed to update group:', err)
            setErrors({ 
                general: err.message || 'Failed to update group. Please try again.' 
            })
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        if (!loading) {
            onClose()
        }
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Edit Group</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* Group Information Display */}
                    {group && (
                        <div className={styles.groupInfo}>
                            <div className={styles.groupIcon}>
                                👥
                            </div>
                            <div className={styles.groupDetails}>
                                <h3>Editing: {group.name}</h3>
                                <p>{group.member_count || 0} members • Created {formatDisplayDate(group.created_at)}</p>
                            </div>
                        </div>
                    )}

                    {/* General Error */}
                    {errors.general && (
                        <div className={styles.errorMessage}>
                            {errors.general}
                        </div>
                    )}

                    {/* Group Name Field */}
                    <div className={styles.formGroup}>
                        <label htmlFor="groupName" className={styles.label}>
                            Group Name *
                        </label>
                        <input
                            type="text"
                            id="groupName"
                            className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            disabled={loading}
                            placeholder="Enter group name"
                            required
                        />
                        {errors.name && (
                            <span className={styles.fieldError}>{errors.name}</span>
                        )}
                    </div>

                    {/* Description Field */}
                    <div className={styles.formGroup}>
                        <label htmlFor="groupDescription" className={styles.label}>
                            Description
                        </label>
                        <textarea
                            id="groupDescription"
                            className={`${styles.textarea} ${errors.description ? styles.inputError : ''}`}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            disabled={loading}
                            placeholder="Enter group description (optional)"
                            rows="3"
                        />
                        {errors.description && (
                            <span className={styles.fieldError}>{errors.description}</span>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className={styles.buttonRow}>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={loading}
                            >
                                {loading ? 'Updating...' : 'Update Group'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}